import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import HiredLogo from "@/components/brand/HiredLogo";
import { Home, Users, ListChecks, ShieldCheck, Activity, PlugZap, FileText, BarChart3, CreditCard, Settings, HelpCircle, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Home", icon: Home, testid: "sidenav-home" },
  { to: "/employees", label: "My Employees", icon: Users, testid: "sidenav-employees" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "sidenav-tasks" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, testid: "sidenav-approvals" },
  { to: "/activity", label: "Activity", icon: Activity, testid: "sidenav-activity" },
  { to: "/integrations", label: "Integrations", icon: PlugZap, testid: "sidenav-integrations" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "sidenav-documents" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "sidenav-reports" },
  { to: "/billing", label: "Billing", icon: CreditCard, testid: "sidenav-billing" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "sidenav-settings" },
  { to: "/help", label: "Help", icon: HelpCircle, testid: "sidenav-help" },
];

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => { logout(); nav("/"); };

  const Side = (
    <aside className="w-64 shrink-0 border-r border-line bg-obsidian min-h-screen py-6 px-3 flex flex-col">
      <Link to="/" data-testid="dash-brand" className="px-3 mb-8"><HiredLogo size={20} /></Link>
      <nav className="flex-1 space-y-1">
        {NAV.map(({ to, label, icon: Icon, testid }) => {
          const active = loc.pathname === to || loc.pathname.startsWith(to + "/");
          return (
            <Link key={to} to={to} data-testid={testid} onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${active ? "bg-graphite text-ivory border-l-2 border-gold" : "text-steel hover:text-ivory hover:bg-graphite/50"}`}>
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </Link>
          );
        })}
      </nav>
      <button data-testid="dash-logout" onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-steel hover:text-ivory mt-4">
        <LogOut size={16} strokeWidth={1.5} /> Log out
      </button>
    </aside>
  );

  return (
    <div className="min-h-screen bg-obsidian text-ivory flex">
      <div className="hidden md:block">{Side}</div>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="relative">{Side}</div>
        </div>
      )}
      <main className="flex-1 min-w-0">
        <div className="md:hidden border-b border-line px-4 py-3 flex items-center justify-between sticky top-0 bg-obsidian z-30">
          <Link to="/"><HiredLogo size={18} /></Link>
          <button data-testid="mobile-nav-toggle" onClick={() => setOpen(v => !v)} className="text-ivory">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        <div className="px-6 md:px-10 py-8 md:py-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
